import { Package, Clock, Archive, ChevronDown, ChevronUp } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { OohTrip, OohCourierVisit } from '../../../../core/graphql/types';

interface DeliveryTripCardProps {
  locationTrip: OohTrip;
  tripIndex: number;
  isExpanded: boolean;
  onToggleExpansion: () => void;
}

const DeliveryTripCard = ({
  locationTrip,
  tripIndex,
  isExpanded,
  onToggleExpansion,
}: DeliveryTripCardProps) => {
  const calculateTripMetrics = () => ({
    charges: locationTrip.oohCourierVisits.reduce(
      (acc: number, curr: OohCourierVisit) => acc + (curr.visitCharges ?? 0),
      0,
    ),
    dischargesC2C: locationTrip.oohCourierVisits.reduce(
      (acc: number, curr: OohCourierVisit) =>
        acc + (curr.visitDischargesC2C ?? 0),
      0,
    ),
    dischargesFirstMile: locationTrip.oohCourierVisits.reduce(
      (acc: number, curr: OohCourierVisit) =>
        acc + (curr.visitDischargesFirstMile ?? 0),
      0,
    ),
    dischargesExpired: locationTrip.oohCourierVisits.reduce(
      (acc: number, curr: OohCourierVisit) =>
        acc + (curr.visitDischargesExpired ?? 0),
      0,
    ),
    dischargesReturn: locationTrip.oohCourierVisits.reduce(
      (acc: number, curr: OohCourierVisit) =>
        acc + (curr.visitDischargesReturn ?? 0),
      0,
    ),
  });

  const metrics = calculateTripMetrics();

  return (
    <div className="rounded-sm border border-blue bg-light-blue dark:border-bright-blue dark:bg-night-dark-blue">
      <div className="flex items-start gap-4 p-2">
        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-bright-blue text-sm font-bold text-white">
          {tripIndex + 1}
        </div>
        <div className="flex-1">
          <div className="mb-1 flex items-center gap-2 text-sm text-design-gray dark:text-gray">
            <Clock className="h-4 w-4" />
            <span>
              {format(parseISO(locationTrip.warehouseDepartureTime), 'HH:mm')}
              {' - '}
              {format(parseISO(locationTrip.warehouseReturnTime), 'HH:mm')}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-design-black-accent dark:text-gray">
            <span className="flex items-center gap-1">
              <Package className="h-4 w-4 text-bright-green " />
              {`${metrics.charges} charges`}
            </span>
            <span className="flex items-center gap-1">
              <Package className="h-4 w-4 text-bright-blue" />
              {`${metrics.dischargesC2C} C2C discharges`}
            </span>
            <span className="flex items-center gap-1">
              <Package className="d h-4 w-4 text-orange-500" />
              {`${metrics.dischargesFirstMile} FM discharges`}
            </span>
            <span className="flex items-center gap-1">
              <Package className="h-4 w-4 text-red dark:text-red" />
              {`${metrics.dischargesExpired} Expired discharges`}
            </span>
            <span className="flex items-center gap-1">
              <Package className="h-4 w-4 text-purple-500" />
              {`${metrics.dischargesReturn} Return discharges`}
            </span>
          </div>
        </div>
        <button
          onClick={onToggleExpansion}
          className="flex-shrink-0 rounded p-1 transition-colors duration-200 hover:bg-bright-blue/30 dark:hover:bg-bright-blue/30"
          aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
        >
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-bright-blue" />
          ) : (
            <ChevronDown className="h-4 w-4 text-bright-blue" />
          )}
        </button>
      </div>

      {isExpanded && (
        <div className=" border-t border-bright-blue p-3 dark:border-blue ">
          <div className="mb-3 flex items-center gap-2">
            <Archive className="h-4 w-4 text-bright-blue" />
            <span className="text-sm font-medium dark:text-gray">
              Locker Actions
            </span>
          </div>
          <div className="space-y-2">
            {locationTrip.oohCourierVisits.length > 0 ? (
              locationTrip.oohCourierVisits.map(
                (oohCourierVisit: OohCourierVisit, index: number) => (
                  <div
                    key={index}
                    className="flex items-center justify-between rounded border border-blue  p-2 dark:border-blue dark:bg-light-blue"
                  >
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-black"></div>
                      <span className="text-sm font-medium text-black">
                        {oohCourierVisit.oohPoint?.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span
                        className="flex items-center gap-1 font-medium text-bright-green"
                        title={`${
                          oohCourierVisit.visitCharges ?? 0
                        } charges into the OOH point`}
                      >
                        <Package className="h-3 w-3" />
                        {oohCourierVisit.visitCharges ?? 0}
                      </span>
                      <span
                        className="flex items-center gap-1 font-medium text-bright-blue"
                        title={`${
                          oohCourierVisit.visitDischargesC2C ?? 0
                        } C2C discharges from the OOH point`}
                      >
                        <Package className="h-3 w-3" />
                        {oohCourierVisit.visitDischargesC2C ?? 0}
                      </span>
                      <span
                        className="flex items-center gap-1 font-medium text-orange-500"
                        title={`${
                          oohCourierVisit.visitDischargesFirstMile ?? 0
                        } First Mile discharges from the OOH point`}
                      >
                        <Package className="h-3 w-3" />
                        {oohCourierVisit.visitDischargesFirstMile ?? 0}
                      </span>
                      <span
                        className="flex items-center gap-1 font-medium text-red"
                        title={`${
                          oohCourierVisit.visitDischargesExpired ?? 0
                        } Expired discharges from the OOH point`}
                      >
                        <Package className="h-3 w-3" />
                        {oohCourierVisit.visitDischargesExpired ?? 0}
                      </span>
                      <span
                        className="flex items-center gap-1 font-medium text-purple-500"
                        title={`${
                          oohCourierVisit.visitDischargesReturn ?? 0
                        } Returned discharges from the OOH point`}
                      >
                        <Package className="h-3 w-3" />
                        {oohCourierVisit.visitDischargesReturn ?? 0}
                      </span>
                    </div>
                  </div>
                ),
              )
            ) : (
              <div className="flex items-center justify-center p-4">
                No courier actions found at OOH points
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryTripCard;
